import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const collectionModes = ["CASH", "UPI", "CREDIT"];

const statusBadgeClass = {
  PLANNED: "badge-gray",
  LOADED: "badge-blue",
  IN_PROGRESS: "badge-amber",
  COMPLETED: "badge-green",
};

const invoiceStatusBadgeClass = {
  PENDING: "badge-gray",
  DELIVERED: "badge-green",
  PARTIALLY_DELIVERED: "badge-amber",
  RETURNED: "badge-red",
  EXPIRED: "badge-red",
  RESCHEDULED: "badge-amber",
};

const LoadingSheets = () => {
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [generateForm, setGenerateForm] = useState({ deliveryBoyId: "", deliveryDate: "", vehicleNumber: "" });
  const [generating, setGenerating] = useState(false);
  const [lookupId, setLookupId] = useState("");
  const [sheet, setSheet] = useState(null);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [manifest, setManifest] = useState(null);

  const [deliverInvoice, setDeliverInvoice] = useState(null);
  const [deliverForm, setDeliverForm] = useState({ collectionMode: "CASH", collectedAmount: "", remarks: "" });
  const [deliverQuantities, setDeliverQuantities] = useState({});
  const [delivering, setDelivering] = useState(false);

  const [returnInvoice, setReturnInvoice] = useState(null);
  const [returnItems, setReturnItems] = useState({});
  const [returning, setReturning] = useState(false);

  const loadDeliveryBoys = async () => {
    try {
      const data = await api.get("/api/v1/sales-persons", { type: "DELIVERY_BOY" });
      setDeliveryBoys(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load delivery boys");
    }
  };

  useEffect(() => {
    loadDeliveryBoys();
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const result = await api.post("/api/v1/loading-sheets", {
        deliveryBoyId: Number(generateForm.deliveryBoyId),
        deliveryDate: generateForm.deliveryDate,
        vehicleNumber: generateForm.vehicleNumber || undefined,
      });
      toast.success(`Loading sheet #${result.id} generated`);
      setSheet(result);
      setLookupId(String(result.id));
      setManifest(null);
    } catch (error) {
      toast.error(error.message || "Failed to generate loading sheet");
    } finally {
      setGenerating(false);
    }
  };

  const handleLookup = async () => {
    if (!lookupId) return;
    setLoadingSheet(true);
    try {
      const result = await api.get(`/api/v1/loading-sheets/${lookupId}`);
      setSheet(result);
      setManifest(null);
    } catch (error) {
      toast.error(error.message || "Failed to load sheet");
    } finally {
      setLoadingSheet(false);
    }
  };

  const refreshSheet = async () => {
    if (!sheet) return;
    try {
      const result = await api.get(`/api/v1/loading-sheets/${sheet.id}`);
      setSheet(result);
    } catch (error) {
      toast.error(error.message || "Failed to refresh sheet");
    }
  };

  const openDeliver = (invoice) => {
    setDeliverInvoice(invoice);
    setDeliverForm({ collectionMode: "CASH", collectedAmount: "", remarks: "" });
    const initial = {};
    invoice.items.forEach((item) => {
      initial[item.invoiceItemId] = item.quantity;
    });
    setDeliverQuantities(initial);
  };

  const handleDeliverSubmit = async (e) => {
    e.preventDefault();
    setDelivering(true);
    try {
      const deliveredQuantity = {};
      Object.entries(deliverQuantities).forEach(([itemId, qty]) => {
        deliveredQuantity[itemId] = Number(qty) || 0;
      });
      await api.put(`/api/v1/loading-sheets/${sheet.id}/invoices/${deliverInvoice.id}/deliver`, {
        deliveredQuantity,
        collectedAmount: deliverForm.collectedAmount ? Number(deliverForm.collectedAmount) : 0,
        collectionMode: deliverForm.collectionMode,
        remarks: deliverForm.remarks || null,
      });
      toast.success("Delivery recorded");
      setDeliverInvoice(null);
      refreshSheet();
    } catch (error) {
      toast.error(error.message || "Failed to record delivery");
    } finally {
      setDelivering(false);
    }
  };

  const openReturn = (invoice) => {
    setReturnInvoice(invoice);
    const initial = {};
    invoice.items.forEach((item) => {
      initial[item.invoiceItemId] = { checked: false, returnedQuantity: "", reasonId: "" };
    });
    setReturnItems(initial);
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    const items = Object.entries(returnItems)
      .filter(([, value]) => value.checked && value.returnedQuantity && value.reasonId)
      .map(([invoiceItemId, value]) => ({
        invoiceItemId: Number(invoiceItemId),
        returnedQuantity: Number(value.returnedQuantity),
        reasonId: Number(value.reasonId),
      }));
    if (items.length === 0) {
      toast.error("Select at least one item with quantity and reason ID");
      return;
    }
    setReturning(true);
    try {
      await api.put(`/api/v1/loading-sheets/${sheet.id}/invoices/${returnInvoice.id}/return`, { items, remarks: null });
      toast.success("Return recorded");
      setReturnInvoice(null);
      refreshSheet();
    } catch (error) {
      toast.error(error.message || "Failed to record return");
    } finally {
      setReturning(false);
    }
  };

  const handlePrint = async () => {
    try {
      const result = await api.get(`/api/v1/loading-sheets/${sheet.id}/print`);
      setManifest(result);
    } catch (error) {
      toast.error(error.message || "Failed to load manifest");
    }
  };

  const handleExport = async () => {
    try {
      await api.downloadFile("/api/v1/loading-sheets/export", { loadingSheetId: sheet.id }, `loading-sheet-${sheet.id}.xlsx`);
    } catch (error) {
      toast.error(error.message || "Failed to export manifest");
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <div className="modal-box" style={{ boxShadow: "none", padding: "16px", marginBottom: "16px" }}>
        <h4 style={{ fontWeight: 600, marginBottom: "10px" }}>Generate Loading Sheet</h4>
        <form onSubmit={handleGenerate} className="form-grid" style={{ gridTemplateColumns: "2fr 1fr 1fr auto" }}>
          <select
            value={generateForm.deliveryBoyId}
            onChange={(e) => setGenerateForm((prev) => ({ ...prev, deliveryBoyId: e.target.value }))}
            required
          >
            <option value="">Select delivery boy</option>
            {deliveryBoys.map((p) => (
              <option key={p.id} value={p.id}>
                {p.userName || p.employeeCode} ({p.employeeCode})
              </option>
            ))}
          </select>
          <input
            type="date"
            onClick={(e) => e.target.showPicker?.()}
            value={generateForm.deliveryDate}
            onChange={(e) => setGenerateForm((prev) => ({ ...prev, deliveryDate: e.target.value }))}
            required
          />
          <input
            type="text"
            placeholder="Vehicle Number"
            value={generateForm.vehicleNumber}
            onChange={(e) => setGenerateForm((prev) => ({ ...prev, vehicleNumber: e.target.value }))}
          />
          <button type="submit" className="primary" disabled={generating}>
            {generating ? "Generating..." : "Generate"}
          </button>
        </form>
      </div>

      <div className="filters-bar" style={{ marginTop: 0 }}>
        <input
          type="text"
          placeholder="Look up loading sheet by ID"
          value={lookupId}
          onChange={(e) => setLookupId(e.target.value)}
        />
        <button className="secondary" onClick={handleLookup} disabled={loadingSheet}>
          {loadingSheet ? "Loading..." : "Load Sheet"}
        </button>
        {sheet ? (
          <>
            <button className="secondary" onClick={handlePrint}>
              View Manifest
            </button>
            <button className="secondary" onClick={handleExport}>
              Export Manifest
            </button>
          </>
        ) : null}
      </div>

      {sheet ? (
        <div style={{ marginTop: "16px" }}>
          <div className="page-subtitle">
            Sheet #{sheet.id} · Delivery Boy #{sheet.deliveryBoyId} · {sheet.deliveryDate}
            {sheet.vehicleNumber ? ` · Vehicle ${sheet.vehicleNumber}` : ""} ·{" "}
            <span className={`badge ${statusBadgeClass[sheet.status] || "badge-gray"}`}>{sheet.status}</span>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Invoice #</th>
                  <th>Customer ID</th>
                  <th>Status</th>
                  <th>Items</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(sheet.invoices || []).length === 0 ? (
                  <tr>
                    <td colSpan={6}>No invoices in this loading sheet.</td>
                  </tr>
                ) : (
                  sheet.invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{invoice.sequenceNumber}</td>
                      <td>{invoice.invoiceNumber}</td>
                      <td>{invoice.customerId}</td>
                      <td>
                        <span className={`badge ${invoiceStatusBadgeClass[invoice.deliveryStatus] || "badge-gray"}`}>
                          {invoice.deliveryStatus}
                        </span>
                      </td>
                      <td>{(invoice.items || []).map((i) => `${i.productName} x${i.quantity}`).join(", ")}</td>
                      <td className="action-buttons flex gap-2">
                        <button className="primary" onClick={() => openDeliver(invoice)}>
                          Deliver
                        </button>
                        <button className="close-btn" onClick={() => openReturn(invoice)}>
                          Return
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {manifest ? (
        <div style={{ marginTop: "16px" }}>
          <h4 style={{ fontWeight: 600, marginBottom: "8px" }}>Manifest — {manifest.deliveryDate}</h4>
          <div className="table-wrapper" style={{ marginTop: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Total Quantity</th>
                </tr>
              </thead>
              <tbody>
                {(manifest.lines || []).map((line, idx) => (
                  <tr key={idx}>
                    <td>{line.skuCode}</td>
                    <td>{line.productName}</td>
                    <td>{line.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {deliverInvoice ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setDeliverInvoice(null)}>
          <div className="modal-box" style={{ maxWidth: "600px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Deliver — {deliverInvoice.invoiceNumber}</h3>
              <button className="close-btn" onClick={() => setDeliverInvoice(null)}>
                Close
              </button>
            </div>
            <form onSubmit={handleDeliverSubmit}>
              <div className="table-wrapper" style={{ marginTop: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Ordered</th>
                      <th>Delivered Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliverInvoice.items.map((item) => (
                      <tr key={item.invoiceItemId}>
                        <td>{item.productName}</td>
                        <td>{item.quantity}</td>
                        <td>
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            style={{ width: "100px" }}
                            value={deliverQuantities[item.invoiceItemId] ?? ""}
                            onChange={(e) =>
                              setDeliverQuantities((prev) => ({ ...prev, [item.invoiceItemId]: e.target.value }))
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-grid" style={{ marginTop: "16px" }}>
                <div className="form-group">
                  <label>Collection Mode</label>
                  <select
                    value={deliverForm.collectionMode}
                    onChange={(e) => setDeliverForm((prev) => ({ ...prev, collectionMode: e.target.value }))}
                  >
                    {collectionModes.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Collected Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={deliverForm.collectedAmount}
                    onChange={(e) => setDeliverForm((prev) => ({ ...prev, collectedAmount: e.target.value }))}
                  />
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Remarks</label>
                  <input
                    value={deliverForm.remarks}
                    onChange={(e) => setDeliverForm((prev) => ({ ...prev, remarks: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setDeliverInvoice(null)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={delivering}>
                  {delivering ? "Saving..." : "Mark Delivered"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {returnInvoice ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setReturnInvoice(null)}>
          <div className="modal-box" style={{ maxWidth: "640px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Return — {returnInvoice.invoiceNumber}</h3>
              <button className="close-btn" onClick={() => setReturnInvoice(null)}>
                Close
              </button>
            </div>
            <form onSubmit={handleReturnSubmit}>
              <div className="table-wrapper" style={{ marginTop: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Returned Qty</th>
                      <th>Reason ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnInvoice.items.map((item) => {
                      const current = returnItems[item.invoiceItemId] || {};
                      return (
                        <tr key={item.invoiceItemId}>
                          <td>
                            <input
                              type="checkbox"
                              checked={current.checked || false}
                              onChange={(e) =>
                                setReturnItems((prev) => ({
                                  ...prev,
                                  [item.invoiceItemId]: { ...prev[item.invoiceItemId], checked: e.target.checked },
                                }))
                              }
                            />
                          </td>
                          <td>{item.productName}</td>
                          <td>{item.quantity}</td>
                          <td>
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              style={{ width: "90px" }}
                              value={current.returnedQuantity || ""}
                              onChange={(e) =>
                                setReturnItems((prev) => ({
                                  ...prev,
                                  [item.invoiceItemId]: { ...prev[item.invoiceItemId], returnedQuantity: e.target.value },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              style={{ width: "80px" }}
                              value={current.reasonId || ""}
                              onChange={(e) =>
                                setReturnItems((prev) => ({
                                  ...prev,
                                  [item.invoiceItemId]: { ...prev[item.invoiceItemId], reasonId: e.target.value },
                                }))
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="page-subtitle" style={{ marginTop: "8px" }}>
                Reason ID references the return_reasons table (no lookup endpoint exists yet — enter the numeric ID).
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setReturnInvoice(null)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={returning}>
                  {returning ? "Saving..." : "Record Return"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LoadingSheets;
